import API from './ApiBase';

export type Regulation = {
  regulationCode: string;
  regulationName: string;
  jurisdiction: string;
  regulationPackVersionId: string;
  versionLabel: string;
  versionNumber: number;
  status: string;
  // True when the pack drives a questionnaire (EU); false for assessment-only
  // packs (LL144).
  hasQuestionnaire: boolean;
};

export const fetchRegulations = async (): Promise<Regulation[]> => {
  try {
    const response = await API.get<Regulation[]>('/regulations');
    return response.data;
  } catch (error) {
    console.error('Error fetching regulations:', error);
    throw error;
  }
};

/**
 * Collapses `/regulations` to one row per regulation, keeping the highest
 * published `versionNumber`.
 *
 * The endpoint returns one row per published pack version, so a regulation with
 * two published versions arrives twice - which renders duplicate dropdown
 * options (and duplicate React keys, since options key on `regulationCode`).
 * The highest version wins because version-scoped fields such as
 * `hasQuestionnaire` must reflect the pack a new assessment would actually use.
 *
 * First-appearance order is preserved, and rows without a `regulationCode` are
 * dropped - so callers must handle an empty result even for a non-empty input.
 */
export const dedupeRegulationsByCode = <
  T extends Pick<Regulation, 'regulationCode' | 'versionNumber'>
>(
  regulations: T[]
): T[] => {
  const byCode = new Map<string, T>();

  regulations.forEach((regulation) => {
    if (!regulation.regulationCode) return;

    const existing = byCode.get(regulation.regulationCode);
    // Explicit comparison so the tie-break is a rule, not a side effect of how
    // NaN compares: first seen wins on a tie or a non-numeric version.
    const isNewer =
      !existing ||
      (Number.isFinite(regulation.versionNumber) &&
        (!Number.isFinite(existing.versionNumber) ||
          regulation.versionNumber > existing.versionNumber));

    if (isNewer) byCode.set(regulation.regulationCode, regulation);
  });

  // Map preserves insertion order, and set() on an existing key keeps its
  // original position, so the result follows first appearance.
  return Array.from(byCode.values());
};
